import { createContext, useCallback, useContext, useRef, useState } from "react";

import { useT } from "@/i18n/provider";
import { Button } from "./button";
import { Dialog } from "./dialog";

/**
 * "Are you sure?" before anything that cannot be undone (guide 3.9), with
 * the phone app's exact words, passed in by the page:
 *
 *     const confirm = useConfirm();
 *     if (await confirm({ title: t.cancelSaleConfirm, confirmLabel: t.cancelSale, danger: true })) …
 *
 * Answers true for yes, false for no, Escape or closing it.
 */

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel: string;
  /** Defaults to the catalogue's Cancel. */
  cancelLabel?: string;
  /** The yes button in red: for deleting, cancelling a sale, signing out a pharmacy. */
  danger?: boolean;
}

type Ask = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Ask | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const t = useT();
  const [open, setOpen] = useState<ConfirmOptions | null>(null);
  const answer = useRef<(yes: boolean) => void>(() => {});

  const ask = useCallback<Ask>(
    (options) =>
      new Promise<boolean>((resolve) => {
        // A second question while one is open answers the first with no.
        answer.current(false);
        answer.current = resolve;
        setOpen(options);
      }),
    [],
  );

  function reply(yes: boolean) {
    setOpen(null);
    answer.current(yes);
    answer.current = () => {};
  }

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      {open && (
        <Dialog
          title={open.title}
          onClose={() => reply(false)}
          footer={
            <>
              {/* Focus starts on the safe answer when the yes destroys something:
                  an Enter pressed out of habit must not cancel a sale. */}
              <Button
                variant="secondary"
                label={open.cancelLabel ?? t.cancel}
                onClick={() => reply(false)}
                data-autofocus={open.danger ? "" : undefined}
              />
              <Button
                variant={open.danger ? "danger" : "primary"}
                label={open.confirmLabel}
                onClick={() => reply(true)}
                data-autofocus={open.danger ? undefined : ""}
              />
            </>
          }
        >
          {open.message && <p className="text-base text-body">{open.message}</p>}
        </Dialog>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Ask {
  const ask = useContext(ConfirmContext);
  if (!ask) throw new Error("useConfirm must be used inside a ConfirmProvider");
  return ask;
}
